import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicSign() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [pages, setPages] = useState([]);
  const [page, setPage] = useState(1);
  const [pos, setPos] = useState({ x: 100, y: 100, w: 180, h: 80 });
  const [signerName, setSignerName] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/share/${token}`);
        setMeta(data);
        if (data.status === "pending") {
          const { data: thumbs } = await axios.get(`${API}/share/${token}/thumbnails`);
          setPages(thumbs.pages);
        }
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      }
    })();
  }, [token]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFF";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = "#09090B";
    ctx.lineCap = "round";
  }, [meta]);

  function getPos(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const tt = e.touches?.[0];
    const cx = (tt ? tt.clientX : e.clientX) - rect.left;
    const cy = (tt ? tt.clientY : e.clientY) - rect.top;
    return { x: cx * (c.width / rect.width), y: cy * (c.height / rect.height) };
  }
  function start(e) { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const { x, y } = getPos(e); ctx.beginPath(); ctx.moveTo(x, y); }
  function move(e) { if (!drawing.current) return; const ctx = canvasRef.current.getContext("2d"); const { x, y } = getPos(e); ctx.lineTo(x, y); ctx.stroke(); }
  function end() { drawing.current = false; }
  function clearCanvas() {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFF"; ctx.fillRect(0, 0, c.width, c.height);
  }

  async function submit() {
    setBusy(true);
    try {
      const c = canvasRef.current;
      const blob = await new Promise((resolve) => c.toBlob(resolve, "image/png"));
      const fd = new FormData();
      fd.append("page", page);
      fd.append("x", pos.x); fd.append("y", pos.y);
      fd.append("width", pos.w); fd.append("height", pos.h);
      fd.append("signer_name", signerName);
      fd.append("signature", blob, "signature.png");
      await axios.post(`${API}/share/${token}/sign`, fd);
      setDone(true);
      toast.success(t("share_public_thanks"));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
    setBusy(false);
  }

  if (error) return <div className="min-h-screen grid place-items-center p-8 text-center"><div><div className="hero-h2 text-3xl">⚠</div><p className="mt-2 text-zinc-600">{error}</p></div></div>;
  if (!meta) return <div className="min-h-screen grid place-items-center text-zinc-500" data-testid="public-loading">…</div>;
  if (meta.status === "revoked") return <div className="min-h-screen grid place-items-center p-8"><div className="hero-h2">{t("share_public_revoked")}</div></div>;
  if (meta.status === "signed" || done) return (
    <div className="min-h-screen grid place-items-center p-8 text-center" data-testid="public-thanks">
      <div>
        <div className="hero-display text-5xl">✓</div>
        <h1 className="hero-h2 text-3xl mt-4">{t("share_public_thanks")}</h1>
        <p className="text-zinc-500 mt-2">{meta.filename}</p>
        <a className="mt-6 inline-block underline" href={`${API}/share/${token}/signed`} data-testid="public-download-signed">
          ↓ {t("cta_download")}
        </a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen p-6 md:p-10 max-w-7xl mx-auto" data-testid="public-sign-page">
      <header className="border-b border-zinc-200 pb-4 mb-6">
        <span className="eyebrow">DocuConvert — Public sign</span>
        <h1 className="hero-h2 text-3xl mt-2">{t("share_public_title")}</h1>
        <p className="text-zinc-500 mt-1">{meta.filename}</p>
        {meta.recipient_note && <p className="mt-2 text-zinc-700 italic">"{meta.recipient_note}"</p>}
      </header>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <span className="eyebrow">{t("pdf_signature_label")}</span>
          <canvas ref={canvasRef} width={600} height={220}
            className="sig-canvas mt-2 w-full"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            data-testid="public-canvas" />
          <Button variant="ghost" className="rounded-sm mt-2" onClick={clearCanvas}>{t("cta_clear")}</Button>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div>
              <Label className="eyebrow">{t("share_public_signer")}</Label>
              <Input value={signerName} onChange={(e) => setSignerName(e.target.value)}
                className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="public-signer" />
            </div>
            <div>
              <Label className="eyebrow">{t("pdf_place_on_page")}</Label>
              <Input type="number" value={page} min={1} onChange={(e) => setPage(parseInt(e.target.value) || 1)}
                className="rounded-sm border-zinc-300 h-11 mt-1" />
            </div>
            <div>
              <Label className="eyebrow">{t("pdf_position_x")}</Label>
              <Input type="number" value={pos.x} onChange={(e) => setPos({ ...pos, x: parseFloat(e.target.value) })}
                className="rounded-sm border-zinc-300 h-11 mt-1" />
            </div>
            <div>
              <Label className="eyebrow">{t("pdf_position_y")}</Label>
              <Input type="number" value={pos.y} onChange={(e) => setPos({ ...pos, y: parseFloat(e.target.value) })}
                className="rounded-sm border-zinc-300 h-11 mt-1" />
            </div>
            <div>
              <Label className="eyebrow">{t("pdf_width")}</Label>
              <Input type="number" value={pos.w} onChange={(e) => setPos({ ...pos, w: parseFloat(e.target.value) })}
                className="rounded-sm border-zinc-300 h-11 mt-1" />
            </div>
            <div>
              <Label className="eyebrow">{t("pdf_height")}</Label>
              <Input type="number" value={pos.h} onChange={(e) => setPos({ ...pos, h: parseFloat(e.target.value) })}
                className="rounded-sm border-zinc-300 h-11 mt-1" />
            </div>
          </div>
          <Button onClick={submit} disabled={busy}
            className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11 mt-6"
            data-testid="public-submit">
            {busy ? t("processing") : `${t("pdf_sign_apply")} →`}
          </Button>
        </div>

        <div>
          <span className="eyebrow">Document</span>
          <div className="mt-2 grid grid-cols-3 gap-3 max-h-[600px] overflow-auto pr-2 border border-zinc-200 rounded-md p-3 bg-zinc-50">
            {pages.map((p) => (
              <button key={p.page} onClick={() => setPage(p.page)}
                className={`page-tile ${page === p.page ? "selected" : ""} p-2 rounded-md flex flex-col items-center`}
                data-testid={`public-page-${p.page}`}>
                <img src={p.data_url} alt={`Page ${p.page}`} className="w-full h-auto" />
                <span className="mt-1 eyebrow">P{p.page}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
